import { getDiscountCheckForChildService } from "@/services/parent";

export function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export interface DiscountResult {
  child_discount: number;
  child_discount_amount: number;
  payableAmount: number;
}

export function computeChildDiscount(
  fullAmount: number,
  isDiscountEligible: boolean
): DiscountResult {
  const amount = Number(fullAmount || 0);
  if (!isDiscountEligible) {
    return { child_discount: 0, child_discount_amount: 0, payableAmount: Number(amount.toFixed(2)) };
  }
  const discountAmount = Number((amount * 0.5).toFixed(2));
  return {
    child_discount: 50,
    child_discount_amount: discountAmount,
    payableAmount: Number((amount - discountAmount).toFixed(2)),
  };
}

export function computeChildDiscountByPosition(
  fullAmount: number,
  existingMatchingCount: number,
  index: number
): DiscountResult {
  return computeChildDiscount(fullAmount, existingMatchingCount + index > 0);
}

// ── internal comparison helpers (same as AddChildModal) ─────────────────
const normalizeValue = (value: any): string => String(value ?? "").trim().toLowerCase();

const getRangePeriod = (range: any): string => {
  if (!range) return "";
  if (typeof range === "string") return normalizeValue(range.split("::")[0]);
  return normalizeValue(range.Period);
};

const getRangeKm = (range: any): string => {
  if (!range) return "";
  if (typeof range === "string") return normalizeValue(range.split("::")[1]);
  return normalizeValue(range.KM_Range);
};

const isLocationWithin200Meters = (apiChild: any, currentChild: any): boolean => {
  const apiLat = Number(apiChild?.Latitude);
  const apiLng = Number(apiChild?.Longitude);
  const currentLat = Number(currentChild?.Latitude);
  const currentLng = Number(currentChild?.Longitude);
  if (![apiLat, apiLng, currentLat, currentLng].every(Number.isFinite)) return false;
  return haversineKm(apiLat, apiLng, currentLat, currentLng) * 1000 <= 200;
};

export function compareDiscountChild(apiChild: any, currentChild: any): boolean {
  const stateMatch = normalizeValue(apiChild?.State_Code || apiChild?.State) === normalizeValue(currentChild?.State_Code || currentChild?.State);
  const cityMatch = normalizeValue(apiChild?.City_Code || apiChild?.City) === normalizeValue(currentChild?.City_Code || currentChild?.City);

  const apiSchoolCode = normalizeValue(apiChild?.School_Code);
  const currentSchoolCode = normalizeValue(currentChild?.School_Code);
  const schoolMatch = apiSchoolCode && currentSchoolCode
    ? apiSchoolCode === currentSchoolCode
    : normalizeValue(apiChild?.School) === normalizeValue(currentChild?.School);

  const addressMatch = normalizeValue(apiChild?.Address) === normalizeValue(currentChild?.Address);
  const planMatch = normalizeValue(apiChild?.Plan_Name) === normalizeValue(currentChild?.Plan_Name);
  const periodMatch = getRangePeriod(apiChild?.Range) === getRangePeriod(currentChild?.Range);
  const kmRangeMatch = getRangeKm(apiChild?.Range) === getRangeKm(currentChild?.Range);
  const recordMatch = normalizeValue(apiChild?.Record) === normalizeValue(currentChild?.Record || "Subscribed");
  const locationMatch = isLocationWithin200Meters(apiChild, currentChild);

  return stateMatch && cityMatch && schoolMatch && addressMatch && planMatch && periodMatch && kmRangeMatch && recordMatch && locationMatch;
}

/**
 * Single source of truth for "does this child currently qualify for the
 * sibling discount?" — used by both AddChildModal and the Pay flow.
 */
export async function fetchMatchingDiscountChildren(params: {
  username: string;
  planName: string;
  range: string; // Period, e.g. "Monthly"
  type: "Subscribed" | "Renewal";
  currentChild: any;price:string
}): Promise<any[]> {
  const { username, planName, range, type, currentChild,price } = params;
// console.log(username, planName, range, type, currentChild,'hellll')

  if (!username || !planName?.trim() || !range?.trim()) return [];
  try {
    const response = await getDiscountCheckForChildService(username, planName.trim(), range.trim(), type,price);
    const raw = response?.data as any;
    const list: any[] = Array.isArray(raw)
      ? raw
      : raw?.results ?? raw?.Children ?? raw?.children ?? raw?.Childs ?? raw?.childs ?? [];
    const apiChildren = Array.isArray(list) ? list : [];
    return apiChildren.filter((apiChild) => compareDiscountChild(apiChild, currentChild));
  } catch (error) {
    console.error("Discount API failed:", error);
    return [];
  }
}